import { z } from "zod";

import { audioDiagnosticsSchema } from "@/shared/agent-api/audio-diagnostics";
import { messageRecoveryOutcomeSchema } from "@/shared/agent-api/message-recovery";
import { PatientMessageCategory } from "@/shared/agent-api/patient-message-category";
import { agentCoverageSchema, RecommendationType, searchPatientsResponseSchema } from "@/shared/agent-api/responses";
import {
  SchedulingLinkIntent,
  SchedulingLinkOpportunityStatus,
  SchedulingLinkUnavailableReason,
} from "@/shared/agent-api/scheduling-link";
import {
  SchedulingRequestAdmissionDecision,
  SchedulingScopeStage,
  scopeRequestedIntentSchema,
} from "@/shared/agent-api/scheduling-scope";
import { normalizeLivekitChatHistory, sessionEventsSchema } from "@/shared/agent-api/session-events";
import { intakeSmsConsentSchema, voiceIntakeBookingSchema } from "@/shared/agent-api/slow-thinker-intake-sms";
import { Intent } from "@/shared/agent-prompts/workflow-types";
import {
  appointmentActionIdSchema,
  normalizedEhrIdSchema,
  versionedAppointmentSlotActionIdSchema,
} from "@/shared/common-schemas";
import { VOICE_GROUP_IDS } from "@/shared/group-configs/groups";
import { addressSchema } from "@/shared/models/address";
import {
  AppointmentScopeCategory,
  AppointmentStatus,
  AppointmentType,
  reschedulableStatuses,
  SchedulingAction,
} from "@/shared/models/appointment";
import { ClinicCallReason, legacyClinicCallReasonSchema } from "@/shared/models/clinic-call-reason";
import { RelationshipToSubscriber, relationshipToSubscriberSchema } from "@/shared/models/coverage";
import { PatientCaseType } from "@/shared/models/patient-case";
import { BinaryPersonGender, PersonGender } from "@/shared/models/person";
import { voiceProviderIdentitySchema } from "@/shared/models/provider-name";
import { scheduleAvailabilitySchema } from "@/shared/models/scheduling-availability";
import {
  callerReportedMammogramScreenSchema,
  callerReportedPreviousVisitSchema,
} from "@/shared/scheduling/caller-reported-visit-facts";
import {
  CoverageMaintenance,
  coverageMaintenanceSchema,
  pendingCoverageContextSchema,
  ReportedInsuranceUsage,
  reportedInsuranceUsageSchema,
} from "@/shared/scheduling/coverage-context";
import { northAmericanPhoneNumberSchema, phoneNumberSchema } from "@/shared/utils/phone-utils";
import { voiceExperimentOverridesSchema } from "@/shared/voice-experiments/overrides";
import { sipIngressSessionMetadataSchema } from "@/shared/voice-routing/sip-ingress";

export {
  bookedAppointmentsReadMetadataSchema,
  type BookedAppointmentsReadMetadata,
} from "@/shared/agent-api/booked-appointments-read-metadata";

export enum AppointmentCreatedBy {
  EXTERNAL = "external",
  VALERIE_VOICE = "valerie-voice",
  VALERIE_SMS = "valerie-sms",
  VALERIE_WEB = "valerie-web",
}

/** The authoritative source Voice used when resolving an initial-visit booking reason. */
export enum InitialBookingReasonSource {
  Caller = "caller",
  Referral = "referral",
}

export enum BookedAppointmentsReadPurpose {
  APPOINTMENT_CONFIRMATION = "appointment_confirmation",
  ELIGIBILITY_HISTORY = "eligibility_history",
  PRIOR_VISIT_HISTORY = "prior_visit_history",
  TELEPHONE_ENCOUNTER_ROUTING = "telephone_encounter_routing",
  UNSPECIFIED = "unspecified",
  UPCOMING_BOOKED = "upcoming_booked",
}

export enum BookedAppointmentsConsistency {
  PreferAuthoritative = "prefer_authoritative",
  PreferCached = "prefer_cached",
}

// Preserve the existing serialized-literal DTO types for callers while the enum owns the vocabulary.
export const bookedAppointmentsConsistencySchema = z
  .enum(Object.values<`${BookedAppointmentsConsistency}`>(BookedAppointmentsConsistency))
  .describe(
    "Default prefer_cached uses a safe cache first. Use prefer_authoritative when the caller reports a missing, incorrect, or recently changed appointment, even if other appointments were returned. It tries the live EHR first and may return a safe cached result when the EHR is unavailable; inspect readMetadata.verifiedWithEhr before claiming verification succeeded."
  );

/**
 * How the voice scheduling agent resolved the caller into a patient record.
 * This is an authoritative signal emitted by the agent itself — not inferred
 * from session events downstream.
 */
export enum IntakeType {
  ExistingPatient = "existing_patient",
  NewPatient = "new_patient",
  NewPatientWithReferral = "new_patient_with_referral",
}

export const searchPatientsRequestSchema = z.object({
  groupId: z.string().uuid(),
  calledNumber: phoneNumberSchema.optional(),
  phoneNumber: northAmericanPhoneNumberSchema.describe("Patient mobile phone number").nullish(),
  dateOfBirth: z.string().min(1).describe("Patient date of birth in YYYY-MM-DD format"),
  firstName: z.string().min(1).describe("Patient first name"),
  lastName: z.string().min(1).describe("Patient last name"),
  // Set true when the caller intends to verify on-file insurance with the
  // patient. Triggers an extra EHR coverage fetch, so default off.
  includeCoverage: z.boolean().optional(),
  // TPR Voice uses this after identity verification to resolve a generic
  // booking request from authoritative referral and attended-visit history.
  includeSchedulingContext: z.boolean().optional(),
  // Message-taking workflows use this to decide whether a callback number must be collected.
  // It triggers an authoritative EHR contact lookup, so default off.
  includeContactPhoneStatus: z.boolean().nullish(),
});

export const createPatientRequestSchema = z.object({
  groupId: z.string().uuid(),
  firstName: z.string().min(1),
  lastName: z.string().min(1),
  dateOfBirth: z.string().min(1),
  phone: z.string().min(1),
  middleName: z.string().nullable().optional(),
  gender: z.nativeEnum(PersonGender).optional(),
  homeAddress: addressSchema.optional(),
  email: z.preprocess((val) => (val === "" ? undefined : val), z.string().email().nullish()),
});

export type IntakePatient = z.infer<typeof createPatientRequestSchema>;

/** Caller-confirmed identity used by direct-booking integrations before local persistence exists. */
export const schedulingPatientIdentitySchema = createPatientRequestSchema.pick({
  firstName: true,
  lastName: true,
  dateOfBirth: true,
  phone: true,
});
export type SchedulingPatientIdentity = z.infer<typeof schedulingPatientIdentitySchema>;

export const intakeCoverageSchema = z
  .object({
    payer: z.string().min(1).describe("Insurance payer name."),
    plan: z.string().min(1).nullable().describe("Insurance plan name (e.g. PPO, HMO). Null if not captured."),
    subscriberId: z.string().regex(/^[A-Z0-9]{5,25}$/i),
    relationshipToSubscriber: relationshipToSubscriberSchema.default(RelationshipToSubscriber.SELF),
    /**
     * Sex of the policy holder. Required for every relationship — the EHR
     * layer needs it to file insurance coverage. For SELF coverage, this is
     * also mirrored onto patient.gender server-side.
     */
    subscriberSex: z.nativeEnum(BinaryPersonGender),
    subscriberFirstName: z.string().min(1).optional(),
    subscriberLastName: z.string().min(1).optional(),
  })
  .refine(
    (val) =>
      val.relationshipToSubscriber === RelationshipToSubscriber.SELF ||
      (!!val.subscriberFirstName && !!val.subscriberLastName),
    {
      message: "Subscriber first and last name are required when relationshipToSubscriber is not 'self'",
    }
  );

const confirmedSelfPaySchema = z
  .boolean()
  .optional()
  .describe(
    "The caller explicitly agreed to schedule or reschedule without insurance; the server must recheck coverage."
  );

export const createPatientFromIntakeRequestSchema = createPatientRequestSchema.extend({
  includeMatchedStatus: z.boolean().optional().describe("Return Matched when intake resolves an existing patient."),
  // Null in payerAndPlan flows — coverage is filed in a follow-up call.
  coverage: intakeCoverageSchema.nullable(),
  // Caller-stated payer/plan is used only for an intake eligibility gate. It is not authoritative
  // coverage and must never be filed in the EHR as part of this request. A null payer means the
  // caller explicitly reported having no insurance.
  reportedCoverage: agentCoverageSchema
    .extend({ payer: z.string().min(1).nullable() })
    .strict()
    .optional(),
  locationEhrId: z.string().min(1).nullish(),
  smsConsent: z.boolean().nullish(),
  // Groups that screen new patients' insurance by line need the dialed number, and a plan the
  // office doesn't take creates a chart only after the caller agrees to book as self-pay.
  calledNumber: phoneNumberSchema.optional(),
  confirmedSelfPay: confirmedSelfPaySchema,
});

export type IntakeCoverage = z.infer<typeof intakeCoverageSchema>;
export type CreatePatientFromIntakeRequest = z.infer<typeof createPatientFromIntakeRequestSchema>;

// Appointment request schemas
export const searchOpenAppointmentsRequestSchema = z.object({
  groupId: z.string().uuid(),
  departmentId: z.string().min(1),
  appointmentTypeId: z.string().min(1),
  providerIds: z.array(z.string()).nullable().optional(),
  ignoreSchedulablePermission: z.boolean().nullable().optional(),
  startDate: z.string().min(1),
  endDate: z.string().min(1),
});

export const dateFormatSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Date must be in yyyy-mm-dd format");

export const appointmentStatusSchema = z.nativeEnum(AppointmentStatus);

export const getBookedAppointmentsRequestSchema = z.object({
  groupId: z.string().uuid(),
  calledNumber: phoneNumberSchema.optional(),
  patientId: z.string().min(1),
  readPurpose: z.nativeEnum(BookedAppointmentsReadPurpose).optional(),
  consistency: bookedAppointmentsConsistencySchema.nullish(),
  startDate: dateFormatSchema
    .nullish()
    .describe("Start date in yyyy-mm-dd format. Optional; defaults to current date."),
  endDate: dateFormatSchema
    .nullish()
    .describe("End date in yyyy-mm-dd format. Optional; defaults to 15 months after the start date."),
  appointmentStatuses: z
    .array(appointmentStatusSchema)
    .default(reschedulableStatuses)
    .nullish()
    .describe("Appointment statuses to include. Optional; defaults to reschedulable statuses on the server."),
});

export const cancelAppointmentRequestSchema = z.object({
  groupId: z.string().uuid(),
  calledNumber: phoneNumberSchema.optional(),
  patientId: z.string().min(1),
  appointmentId: appointmentActionIdSchema.describe("Signed appointment action ID or legacy backend ID to cancel"),
  cancellationReason: z.string().nullish().describe("Caller-stated reason for cancelling the appointment"),
});

export const getAllAppointmentTypesRequestSchema = z.object({
  groupId: z.string().uuid(),
});

export const getTopInsurancePackagesRequestSchema = z.object({
  groupId: z.string().uuid(),
});

export const createPatientInsuranceRequestSchema = z.object({
  groupId: z.string().uuid(),
  patientId: z.string().min(1),
  departmentId: z.string().min(1),
  insurancePackageId: z.string().min(1),
  insuranceIdNumber: z.string().min(1),
  insuredFirstName: z.string().min(1),
  insuredLastName: z.string().min(1),
  insuredSex: z.enum(["M", "F"]),
});

export const getLatestReferralRequestSchema = z.object({
  groupId: z.string().uuid(),
  patientId: z.string().min(1),
});

// Scheduling availability schemas — keep in sync with AppointmentType enum values.
export const availabilityAppointmentTypeSchema = z.nativeEnum(AppointmentType);
const pendingCoverageSchema = pendingCoverageContextSchema
  .optional()
  .describe(
    "Caller-reported coverage awaiting staff verification; the server rechecks coverage and never writes it to the EHR."
  );

export const getAvailabilityRequestSchema = z.object({
  intakeCallStartedAt: voiceIntakeBookingSchema.shape.intakeCallStartedAt,
  confirmedSelfPay: confirmedSelfPaySchema,
  pendingCoverage: pendingCoverageSchema,
  groupId: z.string().uuid(),
  calledNumber: phoneNumberSchema.optional(),
  patientId: z.string().min(1),
  practitionerIds: z.array(z.string()),
  sourceAppointmentId: z.string().min(1).optional(),
  rescheduleUnitAppointmentIds: z.array(z.string().min(1)).length(2).optional(),
  locationIds: z.array(z.string()).nullish(),
  scheduleAvailability: scheduleAvailabilitySchema,
  appointmentType: availabilityAppointmentTypeSchema.default(AppointmentType.INITIAL_CONSULTATION),
});

const schedulingOptionsContinuationCursorSchema = z
  .string()
  .min(1)
  .max(65_536)
  .optional()
  .describe("Opaque backend-authenticated cursor returned by the preceding scheduling-options response");
const schedulingOptionsRequestBaseSchema = getAvailabilityRequestSchema
  .omit({ appointmentType: true, patientId: true, sourceAppointmentId: true, rescheduleUnitAppointmentIds: true })
  .extend({
    continuationCursor: schedulingOptionsContinuationCursorSchema,
  });

const getRescheduleOptionsRequestSchema = schedulingOptionsRequestBaseSchema
  .extend({
    operation: z.literal(SchedulingAction.Reschedule),
    patientId: z.string().uuid(),
    sourceAppointmentId: z.string().min(1),
  })
  .strict();

const getScheduleOptionsRequestSchema = schedulingOptionsRequestBaseSchema
  .extend({
    operation: z.literal(SchedulingAction.Schedule),
    appointmentType: availabilityAppointmentTypeSchema,
    appointmentTypeChoiceId: z.string().trim().min(1).optional(),
    patientId: z.string().uuid().optional(),
    patientIdentity: schedulingPatientIdentitySchema.optional(),
  })
  .strict();

/** Voice-facing, operation-specific scheduling-option search. */
export const getSchedulingOptionsRequestSchema = z
  .discriminatedUnion("operation", [getRescheduleOptionsRequestSchema, getScheduleOptionsRequestSchema])
  .superRefine((request, context) => {
    if (request.operation !== SchedulingAction.Schedule) return;

    if (Boolean(request.patientId) === Boolean(request.patientIdentity)) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Exactly one of patientId or patientIdentity is required",
      });
    }
  });

export const getLocationsRequestSchema = z.object({
  groupId: z.string().uuid(),
  calledNumber: phoneNumberSchema.optional(),
  patientId: z.string().min(1),
});

// Book appointment schemas (defined after availabilityAppointmentTypeSchema)
export const appointmentSlotSchema = z
  .object({
    appointmentId: normalizedEhrIdSchema.describe(
      "The slot ID from the availability lookup response. Use the exact value returned by that tool — never invent or guess it."
    ),
    appointmentType: availabilityAppointmentTypeSchema.describe("The appointment type"),
    ehrAppointmentTypeId: z
      .string()
      .min(1)
      .nullish()
      .describe(
        "EHR appointment type id for the slot. Required for EHRs (e.g. Athena/NIRA) that distinguish open vs booked appointment type ids."
      ),
    start: z.string().min(1).describe("ISO start time from availability response"),
    end: z.string().min(1).describe("ISO end time from availability response"),
    practitionerId: z.string().min(1).describe("The practitioner ID from availability response"),
    locationId: z.string().min(1).describe("The location ID from availability response"),
    appointmentSlotActionId: versionedAppointmentSlotActionIdSchema
      .optional()
      .describe("Opaque signed slot reference returned by the availability API"),
  })
  .describe("The full appointment slot to reschedule to, from existingAppointmentLookup");

export const bookAppointmentRequestSchema = z
  .object({
    ...voiceIntakeBookingSchema.shape,
    intakeSms: intakeSmsConsentSchema.optional(),
    confirmedSelfPay: confirmedSelfPaySchema,
    pendingCoverage: pendingCoverageSchema,
    groupId: z.string().uuid(),
    calledNumber: phoneNumberSchema.optional(),
    patientId: z.string().min(1).optional(),
    patientIdentity: schedulingPatientIdentitySchema.optional(),
    appointmentSlot: appointmentSlotSchema,
    livekitRoomId: z.string().min(1).optional(),
    bookingNote: z.string().optional(),
    // Optional during rolling deploys; current Voice callers send it for referral-or-caller policies.
    initialBookingReasonSource: z.nativeEnum(InitialBookingReasonSource).optional(),
    createdBy: z.nativeEnum(AppointmentCreatedBy).default(AppointmentCreatedBy.VALERIE_VOICE),
  })
  .superRefine((request, context) => {
    if (Boolean(request.patientId) === Boolean(request.patientIdentity)) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Exactly one of patientId or patientIdentity is required",
      });
    }
  });

export const rescheduleAppointmentRequestSchema = z.object({
  confirmedSelfPay: confirmedSelfPaySchema,
  pendingCoverage: pendingCoverageSchema,
  groupId: z.string().uuid(),
  calledNumber: phoneNumberSchema.optional(),
  patientId: z.string().min(1),
  currentAppointment: z.union([
    z.object({
      appointmentId: appointmentActionIdSchema,
      // Omitted for historical sources; the backend must reload and authorize the source before writing.
      practitionerId: z.string().min(1).optional(),
    }),
    // Compatibility for callers deployed before the signed action-ID boundary.
    z.object({
      ehrId: normalizedEhrIdSchema,
      practitionerId: z.string().min(1),
    }),
  ]),
  newAppointmentSlot: appointmentSlotSchema,
  note: z.string().nullish(),
  // Mirrors bookAppointmentRequestSchema: lets the route link the new appointment to the call.
  livekitRoomId: z.string().min(1).optional(),
  createdBy: z.nativeEnum(AppointmentCreatedBy).default(AppointmentCreatedBy.VALERIE_VOICE),
});

export const rescheduleAppointmentUnitRequestSchema = z
  .object({
    confirmedSelfPay: confirmedSelfPaySchema,
    pendingCoverage: pendingCoverageSchema,
    groupId: z.string().uuid(),
    calledNumber: phoneNumberSchema.optional(),
    patientId: z.string().min(1),
    moves: z
      .array(rescheduleAppointmentRequestSchema.pick({ currentAppointment: true, newAppointmentSlot: true }))
      .length(2),
    note: z.string().nullish(),
    livekitRoomId: z.string().min(1).optional(),
    createdBy: z.nativeEnum(AppointmentCreatedBy).default(AppointmentCreatedBy.VALERIE_VOICE),
  })
  .superRefine(({ moves }, context) => {
    const sourceIds = moves.map(({ currentAppointment }) => getCurrentAppointmentActionId(currentAppointment));
    const targetIds = moves.map(({ newAppointmentSlot }) => newAppointmentSlot.appointmentId);

    if (new Set(sourceIds).size !== moves.length) {
      context.addIssue({ code: z.ZodIssueCode.custom, message: "Appointment-unit source IDs must be unique" });
    }

    if (new Set(targetIds).size !== moves.length) {
      context.addIssue({ code: z.ZodIssueCode.custom, message: "Appointment-unit target slots must be unique" });
    }
  });

export type BookAppointmentSlot = z.infer<typeof appointmentSlotSchema>;
export type SearchPatientsRequest = z.infer<typeof searchPatientsRequestSchema>;
export type SearchOpenAppointmentsRequest = z.infer<typeof searchOpenAppointmentsRequestSchema>;
export type BookAppointmentRequest = z.infer<typeof bookAppointmentRequestSchema>;
export type GetBookedAppointmentsRequest = z.infer<typeof getBookedAppointmentsRequestSchema>;
export type AvailabilityAppointmentType = z.infer<typeof availabilityAppointmentTypeSchema>;
export type ScheduleAvailability = z.infer<typeof scheduleAvailabilitySchema>;

export function getCurrentAppointmentActionId(
  currentAppointment: z.infer<typeof rescheduleAppointmentRequestSchema>["currentAppointment"]
): string {
  return "appointmentId" in currentAppointment ? currentAppointment.appointmentId : currentAppointment.ehrId;
}

export type GetAvailabilityRequest = z.infer<typeof getAvailabilityRequestSchema>;
export type GetSchedulingOptionsRequest = z.infer<typeof getSchedulingOptionsRequestSchema>;
export type RescheduleAppointmentUnitRequest = z.infer<typeof rescheduleAppointmentUnitRequestSchema>;
export type GetLocationsRequest = z.infer<typeof getLocationsRequestSchema>;

// Eligibility check schemas
export const checkEligibilityRequestSchema = z
  .object({
    callerReportedHospitalFollowUp: z
      .boolean()
      .optional()
      .describe("The caller's answer to the clinic's hospitalization screen for this booking request."),
    callerReportedMammogramScreen: callerReportedMammogramScreenSchema
      .optional()
      .describe("The caller's answers to a screening mammogram's screening questions, for a booking that names it."),
    callerReportedPreviousVisit: callerReportedPreviousVisitSchema
      .optional()
      .describe("When the caller says their previous visit of the requested kind was, for a booking that names it."),
    confirmedSelfPay: confirmedSelfPaySchema,
    coverageMaintenance: coverageMaintenanceSchema.optional(),
    coverageVerificationPending: z.boolean().optional(),
    groupId: z.string().uuid(),
    calledNumber: phoneNumberSchema.optional(),
    patientId: z.string().min(1),
    recommendationType: z.nativeEnum(RecommendationType).optional(),
    appointmentReason: z.string().min(1).optional(),
    confirmedPriorVisitReasons: z
      .array(z.string().trim().min(1).max(500))
      .min(1)
      .max(25)
      .optional()
      .describe(
        "Exact reasons from authoritative attended appointment history that the caller confirmed describe the current follow-up."
      ),
    sourceAppointmentId: z.string().min(1).optional(),
    // Caller-stated insurance, used by the recommendation when the patient has no
    // on-file/referral coverage yet (e.g. payerAndPlan intake defers full coverage).
    payer: z.string().min(1).optional().describe("Insurance payer/carrier name as stated by the caller."),
    plan: z.string().min(1).nullish().describe("Insurance plan type (e.g. PPO, HMO) as stated by the caller."),
    reportedInsuranceUsage: reportedInsuranceUsageSchema.optional(),
  })
  .superRefine(
    (
      {
        callerReportedHospitalFollowUp,
        callerReportedMammogramScreen,
        callerReportedPreviousVisit,
        confirmedPriorVisitReasons,
        coverageMaintenance,
        coverageVerificationPending,
        payer,
        recommendationType,
        reportedInsuranceUsage,
        sourceAppointmentId,
      },
      context
    ) => {
      if (
        callerReportedHospitalFollowUp !== undefined &&
        recommendationType !== RecommendationType.NEW_PATIENT &&
        recommendationType !== RecommendationType.ESTABLISHED_PATIENT
      ) {
        context.addIssue({
          code: "custom",
          path: ["callerReportedHospitalFollowUp"],
          message: "Hospitalization screening is valid only for new or established-patient booking eligibility",
        });
      }

      if (
        callerReportedMammogramScreen !== undefined &&
        recommendationType !== RecommendationType.NEW_PATIENT &&
        recommendationType !== RecommendationType.ESTABLISHED_PATIENT
      ) {
        context.addIssue({
          code: "custom",
          path: ["callerReportedMammogramScreen"],
          message: "Mammogram screening answers are valid only for new or established-patient booking eligibility",
        });
      }

      if (
        callerReportedPreviousVisit !== undefined &&
        recommendationType !== RecommendationType.NEW_PATIENT &&
        recommendationType !== RecommendationType.ESTABLISHED_PATIENT
      ) {
        context.addIssue({
          code: "custom",
          path: ["callerReportedPreviousVisit"],
          message: "A previous-visit report is valid only for new or established-patient booking eligibility",
        });
      }

      if (sourceAppointmentId && recommendationType !== RecommendationType.RESCHEDULING) {
        context.addIssue({
          code: "custom",
          path: ["sourceAppointmentId"],
          message: "Source appointment is valid only for rescheduling eligibility",
        });
      }

      if (confirmedPriorVisitReasons && recommendationType !== RecommendationType.ESTABLISHED_PATIENT) {
        context.addIssue({
          code: "custom",
          path: ["confirmedPriorVisitReasons"],
          message: "Confirmed prior visit reasons are valid only for established-patient eligibility",
        });
      }

      if (reportedInsuranceUsage === ReportedInsuranceUsage.ReplaceStalePrimary && !payer) {
        context.addIssue({
          code: "custom",
          path: ["payer"],
          message: "Replacing stale primary coverage requires a caller-reported payer",
        });
      }

      if (coverageVerificationPending && (!coverageMaintenance || coverageMaintenance === CoverageMaintenance.None)) {
        context.addIssue({
          code: "custom",
          path: ["coverageMaintenance"],
          message: "Pending coverage verification requires a maintenance operation",
        });
      }

      if (
        coverageMaintenance === CoverageMaintenance.ReplacePrimary &&
        reportedInsuranceUsage !== ReportedInsuranceUsage.ReplaceStalePrimary
      ) {
        context.addIssue({
          code: "custom",
          path: ["reportedInsuranceUsage"],
          message: "Primary replacement requires replace-stale-primary usage",
        });
      }

      if (
        reportedInsuranceUsage === ReportedInsuranceUsage.ReplaceStalePrimary &&
        (coverageMaintenance !== CoverageMaintenance.ReplacePrimary || coverageVerificationPending !== true)
      ) {
        context.addIssue({
          code: "custom",
          path: ["coverageVerificationPending"],
          message: "Replacing stale primary coverage requires pending primary-replacement verification",
        });
      }
    }
  );

export type CancelAppointmentRequest = z.infer<typeof cancelAppointmentRequestSchema>;
export type RescheduleAppointmentRequest = z.infer<typeof rescheduleAppointmentRequestSchema>;
export type CheckEligibilityRequest = z.infer<typeof checkEligibilityRequestSchema>;

// Scheduling scope check. Unlike checkEligibility this takes NO patientId — it evaluates a group's
// action-scoped rules against the appointment's reason and/or type so the agent can transfer an
// out-of-scope request (e.g. cancelling a surgery) without collecting/using patient-specific facts.
//
// Backward compatible with the reason-only screen (VAL-1807): `action` is optional (the server
// defaults it to `schedule`), so a body carrying only `appointmentReason` behaves exactly as before.
// (Optional rather than z.default so the inferred type keeps `action` optional for callers.)
export const relevantSchedulingRulesRequestSchema = z
  .object({
    groupId: z.string().uuid(),
    scopeStage: z
      .enum(SchedulingScopeStage)
      .optional()
      .describe(
        "Initial caller-request screening or booked-appointment authorization. Omitted callers use booked-appointment authorization."
      ),
    action: z
      .nativeEnum(SchedulingAction)
      .optional()
      .describe("The action the caller wants to take on the appointment. Server defaults to 'schedule'."),
    appointmentReason: z
      .string()
      .min(1)
      .nullish()
      .describe(
        "The caller's stated reason at the initial request screen; otherwise the booked appointment's reason for cancel/confirm/reschedule. Never invent clinical details for a generic request."
      ),
    appointmentType: z
      .nativeEnum(AppointmentType)
      .nullish()
      .describe(
        "The type of the appointment being acted on (from getBookedAppointments), used to evaluate type-based rules."
      ),
    ehrAppointmentTypeName: z
      .string()
      .min(1)
      .nullish()
      .describe(
        "The clinic's booked appointment type name for the appointment being acted on (from getBookedAppointments)."
      ),
    detectWorkflowRequest: z.boolean().optional(),
    requireRecognizableMedicalReason: z
      .boolean()
      .optional()
      .describe(
        "Whether the caller-facing runtime requires an unambiguously medical reason before proceeding. When false for a structured initial or follow-up scheduling request, non-categorical wording may proceed, but any explicitly named appointment category is still evaluated. Omitted by legacy callers."
      ),
  })
  .refine(
    (v) =>
      Boolean(v.appointmentReason) ||
      v.appointmentType != null ||
      (v.scopeStage === SchedulingScopeStage.CallerRequest && v.action === SchedulingAction.Reschedule),
    {
      message: "Provide appointmentReason and/or appointmentType.",
    }
  );

export type RelevantSchedulingRulesRequest = z.infer<typeof relevantSchedulingRulesRequestSchema>;

export const relevantSchedulingRulesResponseSchema = z.object({
  inScope: z.boolean(),
  /** Present only for configured initial request admission, never appointment authorization. */
  requestAdmissionDecision: z.enum(SchedulingRequestAdmissionDecision).optional(),
  prohibitingRules: z.array(z.string()),
  requestedIntent: scopeRequestedIntentSchema.optional(),
  // The classifier's brief rationale — observability only, so the agent can log WHY a call was
  // transferred (or allowed) to its trace. Optional so agent/backend deploy in any order.
  reasoning: z.string().optional(),
  // Whether the screened reason is an actual clinical symptom/condition (vs an appointment type /
  // scheduling intent / placeholder). The reason screen re-asks when this is false. nullish so
  // agent/backend deploy in any order and a serialized null never fails response parsing; a
  // missing/null value is treated as specific (never blocks — the agent gates on === false).
  reasonIsSpecific: z.boolean().nullish(),
  // Semantic classification from the scope evaluator. Optional so agent and backend deploys remain
  // compatible in either order; consumers fail closed when a blocked verdict lacks the category.
  appointmentCategory: z.nativeEnum(AppointmentScopeCategory).nullish(),
  // False means no authoritative classification (model refusal or model/parse error). It cannot
  // authorize an appointment action, even if the legacy error fallback has inScope=true.
  classificationSucceeded: z.boolean().optional(),
});

export type RelevantSchedulingRulesResponse = z.infer<typeof relevantSchedulingRulesResponseSchema>;

export const createTelephoneEncounterRequestSchema = z.object({
  appointmentRef: z
    .string()
    .min(1)
    .optional()
    .describe("Runtime-owned reference to the patient appointment this message concerns."),
  groupId: z.string().uuid(),
  patientId: z.string().min(1),
  submissionId: z.string().uuid().optional().describe("Stable command identity for retry-safe routed mock encounters."),
  caller: z
    .string()
    .min(1)
    .max(100)
    .describe("Who is calling, as free text (e.g. 'patient', 'patient's mother', 'Dr. Smith's office')."),
  reason: z
    .string()
    .min(1)
    .max(50)
    .describe("Short classification of the request (e.g. 'Rx refill', 'test result', 'insurance question')."),
  message: z
    .string()
    .min(1)
    .max(1000)
    .describe(
      "The caller's professionalized message as free text for staff to action (profanity replaced with neutral language)."
    ),
  clinicCallReason: z
    .nativeEnum(ClinicCallReason)
    .describe("Structured classification of the call; routes the encounter to the right staff inbox per tenant."),
  callReceivedAt: z
    .string()
    .datetime()
    .optional()
    .describe("ISO 8601 UTC timestamp of when the call was received, used to prefix the TE message body."),
});

export type CreateTelephoneEncounterRequest = z.infer<typeof createTelephoneEncounterRequestSchema>;

export const createTelephoneEncounterResponseSchema = z.object({
  success: z.boolean(),
  /** Cobalt's id for the created encounter, when returned. */
  telephoneEncounterId: z.string().nullable(),
});

export type CreateTelephoneEncounterResponse = z.infer<typeof createTelephoneEncounterResponseSchema>;

export const getPatientMedicationsRequestSchema = z.object({
  groupId: z.string().uuid(),
  calledNumber: phoneNumberSchema.optional(),
  patientId: z.string().min(1),
});

export type GetPatientMedicationsRequest = z.infer<typeof getPatientMedicationsRequestSchema>;

export const messageCallbackTimeSchema = z.string().trim().min(1).max(500).nullable();

export enum PatientCaseLanguage {
  English = "English",
  Spanish = "Spanish",
}

const createPatientCaseRequestBaseSchema = z.object({
  urgent: z.boolean().optional(),
  groupId: z.string().uuid(),
  language: z.enum(PatientCaseLanguage).optional(),
  calledNumber: phoneNumberSchema.optional(),
  patientId: z.string().min(1),
  caller: z.string().trim().min(1).max(100),
  callbackName: z
    .string()
    .trim()
    .min(1)
    .max(100)
    .optional()
    .describe("The caller's relationship or organization when a confirmed third party placed the call."),
  reason: z.string().trim().min(1).max(50),
  callbackTime: messageCallbackTimeSchema
    .optional()
    .describe(
      "The caller's preferred callback timing. Null means the caller explicitly has no preference; omitted means it was not captured."
    ),
  callbackNumber: northAmericanPhoneNumberSchema
    .nullable()
    .optional()
    .describe(
      "The caller-confirmed callback number. Null means to use a valid phone number from the patient chart; omitted means no callback number was captured."
    ),
  internalNote: z
    .string()
    .min(1)
    .max(4000)
    .refine((value) => value.trim().length > 0, "Patient-case note cannot be blank")
    .describe(
      "The message exactly as it should appear in the Athena patient case, including an explicit unconfirmed-intake label when submitted with incomplete intake."
    ),
});

export const createPatientCaseRequestSchema = z.discriminatedUnion("caseType", [
  createPatientCaseRequestBaseSchema.extend({
    caseType: z.literal(PatientCaseType.PRESCRIPTION_REFILL),
    medicationId: z.string().min(1),
    medicationEntryId: z.string().min(1).nullable(),
  }),
  createPatientCaseRequestBaseSchema.extend({
    caseType: z.literal(PatientCaseType.PATIENT_MESSAGE),
    testResultsWorkflow: z.literal(true).optional(),
    messageCategory: z.enum(PatientMessageCategory).optional(),
    clinicCallReason: legacyClinicCallReasonSchema.optional(),
  }),
  createPatientCaseRequestBaseSchema.extend({
    caseType: z.literal(PatientCaseType.SCHEDULING_FOLLOW_UP),
    appointmentId: appointmentActionIdSchema
      .optional()
      .describe("Signed appointment action ID or legacy backend ID for the selected appointment being moved"),
  }),
]);

export type CreatePatientCaseRequest = z.infer<typeof createPatientCaseRequestSchema>;

export const sessionReportSchema = z.object({
  audio_recording_started_at: z.number().nullable().optional(),
  chat_history: z.preprocess(normalizeLivekitChatHistory, z.unknown()).optional(),
  enable_user_data_training: z.boolean().optional(),
  events: sessionEventsSchema.default([]),
  job_id: z.string().min(1),
  options: z.object({ sipIngress: sipIngressSessionMetadataSchema.optional() }).passthrough().optional(),
  room: z.string().min(1),
  room_id: z.string().min(1),
  timestamp: z.number().optional(),
});

/**
 * Deterministic, agent-authored summary of what the voice call accomplished.
 * The backend uses this to decide post-call side effects (e.g. firing the
 * patient-intake SMS) without re-deriving them from session events.
 */
export const voiceCallOutcomeSchema = z.object({
  intakeType: z.nativeEnum(IntakeType).describe("How the caller was resolved into a patient record."),
  appointmentBooked: z.boolean().describe("True if an appointment was successfully booked on this call."),
  // Optional while older voice-agent revisions are still able to ingest transcripts. Current
  // agents always emit it; the backend falls back to the successful create tool event when absent.
  patientCreated: z
    .boolean()
    .optional()
    .describe("True if the voice agent successfully created the patient record during this call."),
  schedulingLinkOpportunity: z
    .object({
      patientId: z.string().uuid(),
      intent: z.enum(SchedulingLinkIntent),
      status: z.enum(SchedulingLinkOpportunityStatus),
      unavailableReason: z.enum(SchedulingLinkUnavailableReason).nullable(),
      messageId: z.string().uuid().nullable().describe("The scheduling-link SMS message when it was sent."),
      evaluatedAt: z
        .string()
        .datetime()
        .describe("ISO 8601 UTC timestamp of the opportunity; conversion windows start here, not at call ingest."),
      announced: z
        .boolean()
        .describe(
          "True if a response instructed to mention the text finished playing. The wording is model-generated, so this does not prove the text was named. A sent text with false here was usually confirmed too late to mention."
        ),
    })
    .optional()
    .describe("The terminal-eligibility scheduling-link opportunity and experiment outcome for this call."),
});

export type VoiceCallOutcome = z.infer<typeof voiceCallOutcomeSchema>;

/**
 * Bounded classification of a failed tool call, derived from the backend HTTP
 * response (status / axios error code) at the agent's service boundary. Kept to
 * a small fixed set so it stays a safe, low-cardinality metric label
 * (`error_type` on voice_tool_calls_total). `control_flow` marks intentional
 * recovery/routing; a tool may emit it to override a low-level HTTP class once
 * domain policy is known. `missing_data` is a backend 422: the request is valid
 * but resource data is incomplete (e.g. a patient with no MRN) — an expected transfer, not a fault.
 * `not_configured` means the requested operation has no deployment configuration, before any
 * upstream request is attempted.
 */
export enum ToolErrorType {
  Conflict = "conflict",
  ControlFlow = "control_flow",
  Http4xx = "http_4xx",
  Http5xx = "http_5xx",
  MissingData = "missing_data",
  Network = "network",
  NotConfigured = "not_configured",
  NotFound = "not_found",
  Timeout = "timeout",
  Unknown = "unknown",
}

export const toolErrorTypeSchema = z.enum(ToolErrorType);

/** One failed tool call, keyed by the LiveKit tool callId so the ingestor can join it to its audit row. */
export const toolCallErrorSchema = z.object({
  callId: z.string().min(1),
  errorType: toolErrorTypeSchema,
});

export type ToolCallError = z.infer<typeof toolCallErrorSchema>;

/**
 * One experiment assignment applied to a call. `ExperimentTag` retains one primary assignment in
 * the legacy top-level fields and can carry every independent assignment dimension alongside it.
 * `targeted` marks a targeting-rule match (excluded from lift stats). See
 * livekit/docs/voice-agent-experiments.md.
 */
export const experimentAssignmentSchema = z.object({
  experimentKey: z.string(),
  variantKey: z.string(),
  configOverrides: voiceExperimentOverridesSchema,
  targeted: z.boolean(),
});

export type ExperimentAssignment = z.infer<typeof experimentAssignmentSchema>;

export const experimentTagSchema = experimentAssignmentSchema.extend({
  /** Every independent decision applied to the call. The top-level fields remain the primary
   * assignment for backwards-compatible transcript columns and existing analytics. */
  assignments: z.array(experimentAssignmentSchema).min(1).optional(),
});

export type ExperimentTag = z.infer<typeof experimentTagSchema>;

// Shadow instrumentation for the stall gate (VAL-2121): one entry per watched workflow action
// the call passed through, with the highest no-progress streak it reached there. Observe-only —
// it does not change when the gate transfers. Recorded per action (not pre-grouped) so the
// safe thresholds can be read from real distributions and actions clustered empirically; for a
// candidate threshold K, "would a gate at K have tripped in this action?" is maxNoProgressStreak >= K.
export const stallMetricEntrySchema = z.object({
  // The Action enum value (e.g. "newPatientIntake"). String, since the enum lives in livekit.
  action: z.string(),
  maxNoProgressStreak: z.number().int().nonnegative(),
  // Watched caller turns spent in this action (excludes the free baseline turn per visit).
  callerTurns: z.number().int().nonnegative(),
});
export type StallMetricEntry = z.infer<typeof stallMetricEntrySchema>;

/**
 * Models configured for one Voice call. LLM roles are explicit because the legacy agent may have
 * a fallback while the v2 agent uses separate thinker, talker, and session-control clients.
 * This records configuration; per-request trace spans remain authoritative for which fallback
 * actually handled an individual request.
 */
export const voiceLlmModelSchema = z.object({
  role: z.enum(["primary", "fallback", "thinker", "talker", "session_control", "voice"]),
  model: z.string().min(1),
  provider: z.string().min(1).optional(),
});

export type VoiceLlmModel = z.infer<typeof voiceLlmModelSchema>;

export const voiceModelConfigurationSchema = z.object({
  sttModel: z.string().min(1),
  ttsModel: z.string().min(1),
  llmModels: z.array(voiceLlmModelSchema).min(1),
});

export type VoiceModelConfiguration = z.infer<typeof voiceModelConfigurationSchema>;

export const ingestTranscriptPayloadSchema = z.object({
  // Additive telemetry must not reject a transcript during independent agent/backend rollouts.
  audioDiagnostics: audioDiagnosticsSchema.optional().catch(undefined),
  messageRecovery: messageRecoveryOutcomeSchema.optional(),
  audioPath: z.string().min(1).optional(),
  calledNumber: z.string().nullable().optional(),
  callOutcome: voiceCallOutcomeSchema.optional(),
  // Runtime-neutral final intent authored by the agent. Optional so backend and
  // agent revisions remain compatible during rolling deploys.
  callerIntent: z.nativeEnum(Intent).nullable().optional(),
  experiment: experimentTagSchema.nullable().optional(),
  groupId: z.string().uuid(),
  // Optional at the HTTP boundary for compatibility with agents deployed before VAL-2576.
  // Current agents always send it.
  modelConfiguration: voiceModelConfigurationSchema.optional(),
  patientId: z.string().uuid().nullable().optional(),
  phoneNumber: z.string().nullable().optional(),
  // The agent and backend deploy independently, so a ledger this backend cannot parse (a newer
  // schema version or enum value) must not reject the transcript. It is validated where persisted.
  runtimeIntentLedger: z.unknown().optional(),
  sessionReport: sessionReportSchema,
  // True when the call ended in a transfer the stall gate initiated (agent/stalled-agent.ts)
  // rather than a model-invoked transferCallToRepresentative tool call. This remains a
  // fallback when synthetic audit recording fails and for older agents. See VAL-2051.
  stalledTransfer: z.boolean().optional(),
  // Shadow stall metric (VAL-2121): per-action max no-progress streak, for setting the gate's
  // thresholds from real traffic. Observe-only; does not affect transfer behavior.
  stallMetric: z.array(stallMetricEntrySchema).optional(),
  // Enrolled test calls persist evaluation telemetry but suppress operational effects such as
  // alerts, engagement writes, intake messages, and CSAT.
  suppressEvaluationSideEffects: z.boolean().optional(),
  // Tool failures classified at the agent boundary, joined to audit rows by callId.
  toolErrors: z.array(toolCallErrorSchema).optional(),
  // LiveKit OTel spans for this session (agent_turn/llm/tts/function_tool/eou/...). Heavy, opaque
  // passthrough payload the endpoint doesn't inspect at the boundary — kept as z.unknown() per the
  // boundary-validation rule (avoids validation overhead on large trace payloads); parsed/validated
  // downstream in processLivekitTraceJson. Optional so trace-disabled sessions still ingest.
  traces: z.unknown().optional(),
  // Deterministic GCS path of the archived raw OTLP trace (gzipped), uploaded by the agent
  // (VAL-2012). Persisted as livekit_session_trace.gcs_trace_path so a trace can be re-parsed later,
  // and used as the ingest dedup key. Reported regardless of upload success (the object may be
  // absent if the fail-open upload failed). Absent for trace-disabled sessions / pre-VAL-2012 agents.
  tracePath: z.string().min(1).optional(),
});

export type IngestTranscriptPayload = z.infer<typeof ingestTranscriptPayloadSchema>;
export type SessionReport = z.infer<typeof sessionReportSchema>;

export const getProviderNamesResponseSchema = z.object({
  providerNames: z.array(z.string()),
  // Absent from web deploys that predate structured provider names.
  providers: z.array(voiceProviderIdentitySchema).optional(),
});

export type GetProviderNamesResponse = z.infer<typeof getProviderNamesResponseSchema>;

export const getClinicFaqRequestSchema = z.object({
  groupId: z.enum(VOICE_GROUP_IDS),
  calledNumber: phoneNumberSchema.nullable().optional(),
});

export type GetClinicFaqRequest = z.infer<typeof getClinicFaqRequestSchema>;

export const getClinicFaqResponseSchema = z.object({
  faq: z.string(),
});

export type GetClinicFaqResponse = z.infer<typeof getClinicFaqResponseSchema>;

export const getOfficeDirectoryRequestSchema = z.object({
  groupId: z.enum(VOICE_GROUP_IDS),
  calledNumber: phoneNumberSchema,
});

export type GetOfficeDirectoryRequest = z.infer<typeof getOfficeDirectoryRequestSchema>;

export const officeDirectoryOfficeSchema = z.object({
  locationId: z.string().min(1),
  displayName: z.string().min(1),
  /** The office's main line; null when none valid is on file, so callers can't be transferred there. */
  phoneNumber: phoneNumberSchema.nullable(),
  /** Whether the called line schedules for this office. */
  isHomeOffice: z.boolean(),
});

export type OfficeDirectoryOffice = z.infer<typeof officeDirectoryOfficeSchema>;

export const officeDirectoryProviderSchema = voiceProviderIdentitySchema.extend({
  practitionerId: z.string().min(1),
  /** Offices where the provider sees patients. */
  locationIds: z.array(z.string().min(1)).min(1),
});

export type OfficeDirectoryProvider = z.infer<typeof officeDirectoryProviderSchema>;

/** Where a group's receiving providers see patients, from its practitioner_location links. */
export const getOfficeDirectoryResponseSchema = z.object({
  offices: z.array(officeDirectoryOfficeSchema),
  providers: z.array(officeDirectoryProviderSchema),
});

export type GetOfficeDirectoryResponse = z.infer<typeof getOfficeDirectoryResponseSchema>;

// Call-startup lookup that maps the caller's phone number to any patients on file,
// so their names can be fed into Deepgram STT keyterms.
export const getPatientNamesByPhoneRequestSchema = z.object({
  groupId: z.string().uuid(),
  phoneNumber: northAmericanPhoneNumberSchema,
});

export type GetPatientNamesByPhoneRequest = z.infer<typeof getPatientNamesByPhoneRequestSchema>;

// Each entry is a full display name (e.g. "Jane Doe"); the keyterm builder tokenizes them.
export const getPatientNamesByPhoneResponseSchema = z.object({
  patientNames: z.array(z.string()),
});

export type GetPatientNamesByPhoneResponse = z.infer<typeof getPatientNamesByPhoneResponseSchema>;

export enum PatientPhoneLookupStatus {
  Ready = "ready",
  Unavailable = "unavailable",
}

export const getPatientCandidatesByPhoneRequestSchema = searchPatientsRequestSchema
  .pick({
    calledNumber: true,
    includeCoverage: true,
    includeSchedulingContext: true,
    includeContactPhoneStatus: true,
  })
  .extend({
    groupId: z.enum(VOICE_GROUP_IDS),
    phoneNumber: northAmericanPhoneNumberSchema,
  });

export type GetPatientCandidatesByPhoneRequest = z.infer<typeof getPatientCandidatesByPhoneRequestSchema>;

/** More records than this on one phone mark a shared line, so the lookup reports unavailable. */
export const MAX_PATIENT_PHONE_CANDIDATES = 5;

// These are unconfirmed records, not evidence that the caller is the patient. Keep DOB
// in private call state and require the caller to supply it before verifying identity.
export const getPatientCandidatesByPhoneResponseSchema = z.discriminatedUnion("status", [
  z.object({
    status: z.literal(PatientPhoneLookupStatus.Ready),
    candidates: z.array(searchPatientsResponseSchema).max(MAX_PATIENT_PHONE_CANDIDATES),
  }),
  z.object({
    status: z.literal(PatientPhoneLookupStatus.Unavailable),
    candidates: z.array(searchPatientsResponseSchema).length(0),
  }),
]);

export type GetPatientCandidatesByPhoneResponse = z.infer<typeof getPatientCandidatesByPhoneResponseSchema>;

export const submitVoiceCsatPayloadSchema = z.object({
  groupId: z.string().uuid(),
  patientId: z.string().uuid().nullish(),
  calledFromNumber: z.string().nullish(),
  livekitRoomId: z.string(),
  score: z.number().int().min(1).max(5),
  qualitativeFeedback: z.string().nullish(),
});

export type SubmitVoiceCsatPayload = z.infer<typeof submitVoiceCsatPayloadSchema>;
